import SaveAsIcon from "@mui/icons-material/SaveAsRounded";
import { Box } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import SplitButton from "@entrilia/shared/components/SplitButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import type { Report } from "../../../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../../../context/useClientContext.ts";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext";
import useCurrentReportConfiguration from "../../../../hooks/useCurrentReportConfiguration";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectReportEditMode } from "../../../../store/selectors/currentReportSelectors";
import { selectVersionHistoryOpen } from "../../../../store/selectors/uiSettingsSelectors.ts";
import { setEditReport } from "../../../../store/thunks/currentReportThunks";
import { getVersionHistory } from "../../../../store/thunks/versionHistoryStateThunks.ts";
import { getPreconditionFailedMessage } from "../../../../utilities/getPreconditionFailedMessage.ts";
import SaveAsDialog from "./SaveAsDialog";
import SaveReportDialog from "./SaveReportDialog";

const SAVE_ERROR_MESSAGE = "An error occurred while saving the report.";

interface Props {
  report: Report;
  allowsSaveAs?: boolean;
  disabled?: boolean;
}

export const SaveButton = ({ report, allowsSaveAs = true, disabled }: Props) => {
  const { addReport, updateReport, getEditReportUrl, getViewReportUrl, getReportHistory } =
    useEnhancedBiApiClientProvider();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { clientCode } = useClientContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditMode = useSelector(selectReportEditMode);
  const versionHistoryOpen = useSelector(selectVersionHistoryOpen);
  const [openSavingDialog, setOpenSavingDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openSaveAsDialog, setOpenSaveAsDialog] = useState<boolean>();
  const isNewReport = report.isDraft === true;

  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const saveExistedReport = useCallback(async () => {
    const reportConfiguration = await getCurrentReportConfigurationAsync();

    if (reportConfiguration === undefined) {
      return;
    }

    setSaving(true);
    const rep = { ...report };
    rep.configuration = reportConfiguration;

    updateReport(rep)
      .then((response) => {
        if (response.success && response.data !== undefined) {
          dispatch(setEditReport(response.data));
          if (versionHistoryOpen) {
            dispatch(getVersionHistory(getReportHistory));
          }
          sendNotification("The report was successfully saved.");
        } else {
          sendNotificationError(response.error?.message || SAVE_ERROR_MESSAGE);
        }
      })
      .catch((error: unknown) => {
        sendNotificationError(getPreconditionFailedMessage(error) ?? SAVE_ERROR_MESSAGE);
      })
      .finally(() => setSaving(false));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    getCurrentReportConfigurationAsync,
    report,
    sendNotification,
    sendNotificationError,
    updateReport,
    getReportHistory,
    versionHistoryOpen,
  ]);

  const onSaveReport = useCallback(() => {
    if (isNewReport) {
      setOpenSavingDialog(true);
    } else {
      saveExistedReport();
    }
  }, [isNewReport, saveExistedReport]);

  const onSaveAs = useCallback(
    (reportId: string) => {
      navigate(isEditMode ? getEditReportUrl(clientCode, reportId) : getViewReportUrl(clientCode, reportId));
    },
    [isEditMode, navigate, getEditReportUrl, getViewReportUrl, clientCode]
  );

  const secondaryOptions = useMemo(() => {
    if (!allowsSaveAs) {
      return [];
    }

    return [{ title: "Save As...", handler: () => setOpenSaveAsDialog(true), icon: SaveAsIcon }];
  }, [setOpenSaveAsDialog, allowsSaveAs]);

  const primaryOption = useMemo(() => ({ title: "Save", handler: onSaveReport }), [onSaveReport]);

  const addNewReport = async (report: Report) => {
    const resp = await addReport(report);
    if (resp.success) {
      return resp.data;
    }

    throw new Error(resp.error?.message);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexGrow: 0,
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <SplitButton
          saving={saving}
          disabled={disabled}
          secondaryOptions={isNewReport ? [] : secondaryOptions}
          primaryOption={primaryOption}
        />
      </Box>
      <SaveReportDialog
        report={report}
        open={openSavingDialog}
        title="Save Report"
        saveBtnTitle="Save"
        onClose={() => setOpenSavingDialog(false)}
      />
      {openSaveAsDialog && report && (
        <SaveAsDialog
          report={report}
          addReport={addNewReport}
          onChanged={(id: string) => {
            sendNotification("The report was successfully duplicated.");
            onSaveAs.call(null, id);
          }}
          onClose={() => setOpenSaveAsDialog(false)}
        />
      )}
    </>
  );
};

export default SaveButton;
